import React from "react";
import { FiCheck } from "react-icons/fi";

import useOutsideClick from "hooks/useClickAway";
import styles from "../FileExplorer.module.scss";

interface DropdownMenuItem {
  /** The button's text, unique within its menu. */
  label: string;
  /** Runs after the menu closes. */
  onSelect: () => void;
  disabled?: boolean;
  /**
   * Set only in a menu of choices: true on the current choice. Every choice
   * gets a check mark, which CSS shows only on the current one.
   */
  isCurrentChoice?: boolean;
}

interface DropdownMenuProps {
  /** The button's accessible name and tooltip. */
  buttonLabel: string;
  buttonClassName: string;
  buttonContent: React.ReactNode;
  /** The menu's accessible name. */
  menuLabel: string;
  items: DropdownMenuItem[];
}

// Gap between the button and its menu.
const MENU_OFFSET_PX = 4;

/** The menu's fixed position, beside its button. */
function menuPositionFor(button: HTMLElement): React.CSSProperties {
  const buttonRect = button.getBoundingClientRect();
  const right = window.innerWidth - buttonRect.right;
  // Open upward from the lower half of the window, to stay on screen.
  if (buttonRect.bottom > window.innerHeight / 2) {
    return {
      right,
      bottom: window.innerHeight - buttonRect.top + MENU_OFFSET_PX,
    };
  }
  return { right, top: buttonRect.bottom + MENU_OFFSET_PX };
}

/**
 * A button that opens a menu of plain buttons: a file's actions, and the
 * toolbar's Sort and View choices. A disclosure, not an ARIA menu, so it needs
 * no arrow-key handling.
 */
export default function DropdownMenu({
  buttonLabel,
  buttonClassName,
  buttonContent,
  menuLabel,
  items,
}: DropdownMenuProps) {
  // Null while the menu is closed.
  const [menuPosition, setMenuPosition] =
    React.useState<React.CSSProperties | null>(null);
  const isMenuOpen = menuPosition !== null;

  // Stable, so useOutsideClick does not re-add its listener on each render.
  const closeMenu = React.useCallback(() => setMenuPosition(null), []);
  const menuRef = useOutsideClick(closeMenu);

  // The menu stays where it opened, so close it when the button moves.
  React.useEffect(() => {
    if (!isMenuOpen) return;
    const closeOnScroll = (event: Event) => {
      // Close only when the scrolled element contains the button, since only
      // that scroll moves it. Other scrolls, such as the chat scrolling
      // during a reply, leave the menu open.
      if (
        event.target instanceof Node &&
        event.target.contains(menuRef.current)
      ) {
        closeMenu();
      }
    };
    window.addEventListener("scroll", closeOnScroll, true);
    window.addEventListener("resize", closeMenu);
    return () => {
      window.removeEventListener("scroll", closeOnScroll, true);
      window.removeEventListener("resize", closeMenu);
    };
  }, [isMenuOpen, closeMenu, menuRef]);

  const toggleMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isMenuOpen) {
      closeMenu();
    } else {
      setMenuPosition(menuPositionFor(event.currentTarget));
    }
  };

  // Closing unmounts the focused item, so focus the button instead.
  const menuButtonRef = React.useRef<HTMLButtonElement>(null);
  const closeMenuAndFocusButton = () => {
    closeMenu();
    menuButtonRef.current?.focus();
  };

  const closeMenuOnEscape = (event: React.KeyboardEvent) => {
    if (isMenuOpen && event.key === "Escape") closeMenuAndFocusButton();
  };

  // Close the menu when keyboard focus leaves it, such as a Tab past the last
  // item. Clicks outside the menu are handled by useOutsideClick.
  const closeMenuOnFocusOut = (event: React.FocusEvent<HTMLDivElement>) => {
    if (
      isMenuOpen &&
      event.relatedTarget instanceof Node &&
      !event.currentTarget.contains(event.relatedTarget)
    ) {
      closeMenu();
    }
  };

  return (
    <div
      ref={menuRef}
      onKeyDown={closeMenuOnEscape}
      onBlur={closeMenuOnFocusOut}
    >
      <button
        ref={menuButtonRef}
        type="button"
        className={buttonClassName}
        onClick={toggleMenu}
        aria-label={buttonLabel}
        aria-expanded={isMenuOpen}
        title={buttonLabel}
      >
        {buttonContent}
      </button>
      {menuPosition !== null && (
        <div
          className={styles.menu}
          style={menuPosition}
          role="group"
          aria-label={menuLabel}
        >
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              className={styles.menuItem}
              onClick={() => {
                closeMenuAndFocusButton();
                item.onSelect();
              }}
              disabled={item.disabled}
              aria-pressed={item.isCurrentChoice}
            >
              {item.isCurrentChoice !== undefined && (
                <FiCheck className={styles.menuItemCheck} aria-hidden="true" />
              )}
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
